import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Modal,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { keyframes } from "@mui/system";

const API_BASE =
  window.location.hostname.includes("cityfarmgame.com")
    ? "https://bigdogbonegame.com/api/bdb/pet"
    : "/api/bdb/pet";
const CITYFARM_URL = "https://www.cityfarmgame.com/";

/* ============================================================
   IMAGES
============================================================ */

const dogImages = {
  idle: "https://res.cloudinary.com/dekoxefky/image/upload/v1780870756/dog_walk_1.png_xhd04p.png",
  walk1: "https://res.cloudinary.com/dekoxefky/image/upload/v1780870756/dog_walk_1.png_xhd04p.png",
  walk2: "https://res.cloudinary.com/dekoxefky/image/upload/v1780870756/dog_walk_1.png_xhd04p.png",
  walk3: "https://res.cloudinary.com/dekoxefky/image/upload/v1780870756/dog_walk_1.png_xhd04p.png",
  eat: "https://res.cloudinary.com/dekoxefky/image/upload/v1780870749/dog_eat.png_t8b4uq.png",
  drink: "https://res.cloudinary.com/dekoxefky/image/upload/v1780870751/dog_drink_d8wpqw.png",
  play: "https://res.cloudinary.com/dekoxefky/image/upload/v1780870752/dog_play.png_kunios.png",
  sleep: "https://res.cloudinary.com/dekoxefky/image/upload/v1780870750/dog_sleep.png_ifeofv.png",
  weak: "https://res.cloudinary.com/dekoxefky/image/upload/v1780870746/dog_fallen.png_dsegnx.png",
  fallen: "https://res.cloudinary.com/dekoxefky/image/upload/v1780870746/dog_fallen.png_dsegnx.png",
};

const ACTION_VIEW_TIME = {
  eat: 6500,
  drink: 6500,
  play: 5500,
  sleep: 6000,
};

const benefitLevels = [
  { level: "1-9", benefit: "+1% Mini-Bone bonus", daily: "Base daily reward" },
  { level: "10-14", benefit: "+2% Mini-Bone bonus", daily: "Higher daily reward" },
  { level: "15-19", benefit: "+2% Mini-Bone + Energy bonus", daily: "Higher daily reward" },
  { level: "20-29", benefit: "+3% Mini-Bone + Walk reduction", daily: "Improved reward" },
  { level: "30-49", benefit: "Adult pet bonuses", daily: "Strong reward" },
  { level: "50+", benefit: "Legendary pet bonuses", daily: "Best reward tier" },
];

/* ============================================================
   ANIMATIONS
============================================================ */

const urgentPulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 rgba(255, 23, 68, 0); }
  50% { transform: scale(1.025); box-shadow: 0 0 22px rgba(255, 23, 68, 0.85); }
  100% { transform: scale(1); box-shadow: 0 0 0 rgba(255, 23, 68, 0); }
`;

const warningPulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 rgba(255, 179, 0, 0); }
  50% { transform: scale(1.015); box-shadow: 0 0 18px rgba(255, 179, 0, 0.7); }
  100% { transform: scale(1); box-shadow: 0 0 0 rgba(255, 179, 0, 0); }
`;

const readyPulse = keyframes`
  0% { box-shadow: 0 0 0 rgba(156, 39, 176, 0); }
  50% { box-shadow: 0 0 18px rgba(156, 39, 176, 0.75); }
  100% { box-shadow: 0 0 0 rgba(156, 39, 176, 0); }
`;

/* ============================================================
   HELPERS
============================================================ */

function getPetImage(pet, animation, walkFrame = 1) {
  if (animation === "eat") return dogImages.eat;
  if (animation === "drink") return dogImages.drink;
  if (animation === "play") return dogImages.play;
  if (animation === "sleep") return dogImages.sleep;

  if (pet?.status === "fallen") return dogImages.fallen;
  if (pet?.status === "weak") return dogImages.weak;

  if (animation === "walk") {
    if (walkFrame === 1) return dogImages.walk1;
    if (walkFrame === 2) return dogImages.walk2;
    return dogImages.walk3;
  }

  return dogImages.idle;
}

function preloadImages() {
  Object.values(dogImages).forEach((src) => {
    const img = new Image();
    img.src = src;
  });
}

function getCookieToken(name = "token") {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
}

function getToken() {
  return (
    sessionStorage.getItem("token") ||
    localStorage.getItem("token") ||
    getCookieToken("token")
  );
}

function petApi() {
  return axios.create({
    baseURL: API_BASE,
    headers: { token: getToken() },
  });
}

function fmtNumber(value) {
  return Number(value || 0).toLocaleString(undefined, {
    maximumFractionDigits: 3,
  });
}

function statusColor(status) {
  if (status === "alive") return "#00e676";
  if (status === "weak") return "#ffb300";
  if (status === "fallen") return "#ff1744";
  return "#ffb300";
}

function statusText(status) {
  if (status === "alive") return "Alive";
  if (status === "weak") return "Weak";
  if (status === "fallen") return "Fallen";
  return status || "Unknown";
}

function stageText(stage) {
  if (stage === "puppy") return "Puppy";
  if (stage === "young") return "Young";
  if (stage === "adult") return "Adult";
  if (stage === "veteran") return "Veteran";
  if (stage === "legendary") return "Legendary";
  return stage || "Puppy";
}

function getBarColor(value) {
  const safeValue = Number(value || 0);
  if (safeValue > 65) return "#00e676";
  if (safeValue > 35) return "#ffb300";
  return "#ff1744";
}

function getUrgency(value, mode = "lowIsBad") {
  const safeValue = Number(value || 0);
  const score = mode === "highIsBad" ? 100 - safeValue : safeValue;

  if (score <= 25) return "critical";
  if (score <= 45) return "warning";
  return "normal";
}

function isCooldownReady(cooldown) {
  if (!cooldown) return true;

  const seconds = Number(cooldown?.seconds ?? cooldown?.remainingSeconds ?? cooldown?.remaining ?? 0);
  const text = String(cooldown?.text || "").toLowerCase();

  return seconds <= 0 && (!text || text === "ready" || text.includes("ready"));
}

function getActionHint(pet, cooldowns) {
  if (!pet) return "Loading puppy status...";
  if (pet.status === "fallen") return "Your puppy is fallen. Revive it first.";

  const needs = [
    { label: "Feed", value: Number(pet.hunger || 0) },
    { label: "Give Water", value: Number(pet.thirst || 0) },
    { label: "Play", value: Number(pet.happiness || 0) },
    { label: "Health", value: Number(pet.health || 0) },
  ].sort((a, b) => a.value - b.value);

  const first = needs[0];

  if (first.value <= 25) return `Critical: use ${first.label} first.`;
  if (first.value <= 45) return `Attention: ${first.label} should be next.`;
  if (isCooldownReady(cooldowns?.claim)) return "Daily reward looks ready. Claim it now.";
  return "Your puppy is stable. Keep the routine active.";
}

function getCurrentBenefitByLevel(level) {
  const lvl = Number(level || 0);

  if (lvl >= 50) return benefitLevels[5];
  if (lvl >= 30) return benefitLevels[4];
  if (lvl >= 20) return benefitLevels[3];
  if (lvl >= 15) return benefitLevels[2];
  if (lvl >= 10) return benefitLevels[1];
  return benefitLevels[0];
}

function StatBar({ label, value, icon }) {
  const safeValue = Math.max(0, Math.min(100, Number(value || 0)));
  const urgency = getUrgency(safeValue);

  return (
    <Box
      sx={{
        width: "100%",
        mb: 0.8,
        px: urgency === "critical" ? 0.6 : 0,
        py: urgency === "critical" ? 0.35 : 0,
        borderRadius: 1.5,
        background: urgency === "critical" ? "rgba(255,23,68,0.08)" : "transparent",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.25 }}>
        <Typography sx={{ color: "white", fontSize: 12, fontWeight: urgency !== "normal" ? 900 : 600 }}>
          {icon} {label}
        </Typography>
        <Typography sx={{ color: "#ffcc80", fontSize: 12, fontWeight: 900 }}>
          {Math.round(safeValue)}%
        </Typography>
      </Box>

      <LinearProgress
        variant="determinate"
        value={safeValue}
        sx={{
          height: 8,
          borderRadius: 10,
          backgroundColor: "rgba(255,255,255,0.16)",
          "& .MuiLinearProgress-bar": {
            borderRadius: 10,
            backgroundColor: getBarColor(safeValue),
          },
        }}
      />
    </Box>
  );
}

function InfoCard({ title, children, sx = {} }) {
  return (
    <Box
      sx={{
        background: "linear-gradient(180deg, rgba(255,255,255,0.09), rgba(255,255,255,0.035))",
        border: "1px solid rgba(255,152,0,0.28)",
        borderRadius: 2.5,
        p: 1.15,
        boxShadow: "inset 0 0 18px rgba(255,152,0,0.04)",
        ...sx,
      }}
    >
      <Typography sx={{ color: "#ffb300", mb: 0.8, fontWeight: 900, fontSize: 13.5 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function SmallRow({ label, value, accent = false }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 0.38 }}>
      <Typography sx={{ color: "rgba(255,255,255,0.74)", fontSize: 12 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          color: accent ? "#ffcc80" : "white",
          fontSize: 12,
          fontWeight: 900,
          textAlign: "right",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function ActionButton({ children, urgency = "normal", readyPulseOn = false, sx = {}, ...props }) {
  const pulseAnimation =
    urgency === "critical"
      ? `${urgentPulse} 1s ease-in-out infinite`
      : urgency === "warning"
        ? `${warningPulse} 1.5s ease-in-out infinite`
        : readyPulseOn
          ? `${readyPulse} 1.6s ease-in-out infinite`
          : "none";

  return (
    <Button
      variant="contained"
      {...props}
      sx={{
        minHeight: 46,
        borderRadius: 2,
        fontWeight: 950,
        letterSpacing: 0.25,
        animation: pulseAnimation,
        border: urgency === "critical" ? "2px solid rgba(255,255,255,0.75)" : "1px solid rgba(255,255,255,0.12)",
        textShadow: "0 1px 2px rgba(0,0,0,0.35)",
        transition: "transform .15s ease, filter .15s ease",
        "&:hover": {
          transform: "translateY(-1px)",
          filter: "brightness(1.08)",
        },
        ...sx,
      }}
    >
      {children}
    </Button>
  );
}

/* ============================================================
   MAIN COMPONENT
============================================================ */

export default function PetOverlay() {
  const [petData, setPetData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openPanel, setOpenPanel] = useState(false);
  const [nickname, setNickname] = useState("");
  const [message, setMessage] = useState(null);
  const [animation, setAnimation] = useState("idle");
  const [walkFrame, setWalkFrame] = useState(1);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);

  const animationTimerRef = useRef(null);

  const pet = petData?.pet;
  const cityFarm = petData?.cityFarm;
  const bdb = petData?.bdb;
  const benefit = petData?.benefit;
  const cooldowns = petData?.cooldowns;

  const displayName = useMemo(() => {
    if (!pet) return "My Puppy";
    return pet.nickname || pet.birthName || "My Puppy";
  }, [pet]);

  const waterQty = cityFarm?.inventory?.Water ?? 0;
  const foodQty = cityFarm?.inventory?.Croquettes ?? 0;
  const claimAmount = benefit?.dailyClaim?.miniHueso || 0;
  const canUseActions = pet?.status !== "fallen";
  const currentLevelBenefit = getCurrentBenefitByLevel(pet?.level || 1);

  const hungerUrgency = getUrgency(pet?.hunger || 0);
  const thirstUrgency = getUrgency(pet?.thirst || 0);
  const happinessUrgency = getUrgency(pet?.happiness || 0);
  const claimReady = isCooldownReady(cooldowns?.claim);
  const priorityHint = getActionHint(pet, cooldowns);

  const notifyGlobalBalanceRefresh = (data) => {
    window.dispatchEvent(
      new CustomEvent("actualizar-saldos", {
        detail: {
          bdb: data?.bdb || null,
          pet: data?.pet || null,
          source: "PetOverlay",
        },
      })
    );
  };

  const clearAnimationTimer = () => {
    if (animationTimerRef.current) {
      clearTimeout(animationTimerRef.current);
      animationTimerRef.current = null;
    }
  };

  const setTimedAnimation = (nextAnimation, duration) => {
    clearAnimationTimer();
    setAnimation(nextAnimation);
    animationTimerRef.current = setTimeout(() => {
      setAnimation("idle");
    }, duration || 3000);
  };

  const refreshPet = async ({ silent = false, keepMessage = true, syncGlobal = true } = {}) => {
    try {
      if (!silent) setLoading(true);

      const res = await petApi().get("/me");

      setPetData(res.data);
      setNickname(res.data?.pet?.nickname || "");
      setLastUpdated(new Date());

      if (syncGlobal) {
        notifyGlobalBalanceRefresh(res.data);
      }

      if (!keepMessage) setMessage(null);

      return res.data;
    } catch (err) {
      console.error("[PET FRONT] load error:", err);

      if (!silent) {
        setMessage({
          type: "error",
          text:
            err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Could not load your puppy.",
        });
      }

      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const updateFromResponseThenRefresh = async (resData) => {
    setPetData((prev) => ({
      ...prev,
      ...resData,
      pet: resData?.pet || prev?.pet,
      benefit: resData?.benefit || prev?.benefit,
      cooldowns: resData?.cooldowns || prev?.cooldowns,
      cityFarm: {
        ...(prev?.cityFarm || {}),
        ...(resData?.cityFarm || {}),
      },
      bdb: {
        ...(prev?.bdb || {}),
        ...(resData?.bdb || {}),
      },
    }));

    notifyGlobalBalanceRefresh(resData);

    await refreshPet({
      silent: true,
      keepMessage: true,
      syncGlobal: true,
    });
  };

  const runAction = async (action, actionAnimation) => {
    try {
      setLoading(true);
      setMessage(null);

      setTimedAnimation(actionAnimation, ACTION_VIEW_TIME[actionAnimation] || 4200);

      await new Promise((resolve) => setTimeout(resolve, 140));

      const res = await petApi().post(`/${action}`);

      setMessage({
        type: "success",
        text: res.data?.message || "Action completed successfully.",
      });

      await updateFromResponseThenRefresh(res.data);
    } catch (err) {
      console.error("[PET FRONT] action error:", err);

      const data = err?.response?.data;

      setMessage({
        type: "error",
        text: data?.cooldown?.text
          ? `${data?.message || "This action is on cooldown."} Wait ${data.cooldown.text}`
          : data?.message || data?.error || "Action failed.",
      });

      await refreshPet({
        silent: true,
        keepMessage: true,
        syncGlobal: true,
      });

      notifyGlobalBalanceRefresh();
      clearAnimationTimer();
      setAnimation("idle");
    } finally {
      setLoading(false);
    }
  };

  const feedPet = () => runAction("feed", "eat");
  const waterPet = () => runAction("water", "drink");
  const playPet = () => runAction("play", "play");
  const claimDaily = () => runAction("claim-daily", "play");

  const saveNickname = async () => {
    try {
      const cleanNickname = String(nickname || "").trim();

      if (!cleanNickname || cleanNickname.length < 2) {
        setMessage({ type: "error", text: "Nickname must have at least 2 characters." });
        return;
      }

      setLoading(true);
      setMessage(null);

      const res = await petApi().post("/nickname", { nickname: cleanNickname });

      setMessage({ type: "success", text: res.data?.message || "Nickname updated." });

      await updateFromResponseThenRefresh(res.data);
    } catch (err) {
      setMessage({
        type: "error",
        text: err?.response?.data?.message || err?.response?.data?.error || "Could not update nickname.",
      });
    } finally {
      setLoading(false);
    }
  };

  const revivePet = async (paymentType) => {
    try {
      setLoading(true);
      setMessage(null);

      setTimedAnimation("play", 3000);

      const res = await petApi().post("/revive", { paymentType });

      setMessage({ type: "success", text: res.data?.message || "Puppy revived successfully." });

      await updateFromResponseThenRefresh(res.data);
    } catch (err) {
      setMessage({
        type: "error",
        text: err?.response?.data?.message || err?.response?.data?.error || "Could not revive your puppy.",
      });

      await refreshPet({ silent: true, keepMessage: true, syncGlobal: true });
      notifyGlobalBalanceRefresh();
      clearAnimationTimer();
      setAnimation("idle");
    } finally {
      setLoading(false);
    }
  };

  const openCityFarm = () => {
    window.open(CITYFARM_URL, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    preloadImages();
    refreshPet();

    const interval = setInterval(() => {
      refreshPet({ silent: true, keepMessage: true, syncGlobal: true });
    }, 60000);

    return () => {
      clearInterval(interval);
      clearAnimationTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const frameInterval = setInterval(() => {
      setWalkFrame((prev) => (prev >= 3 ? 1 : prev + 1));
    }, 450);

    return () => clearInterval(frameInterval);
  }, []);

  if (!pet) return null;

  const floatingImage = getPetImage(pet, animation === "idle" ? "walk" : animation, walkFrame);
  const panelImage = getPetImage(pet, animation, walkFrame);

  return (
    <>
      {/* ======================================================
          FLOATING SPHERE DOG
      ====================================================== */}
      <Tooltip title="Open puppy panel" arrow>
        <Box
          onClick={() => setOpenPanel(true)}
          sx={{
            position: "fixed",
            left: { xs: 10, sm: 25 },
            bottom: { xs: 54, sm: 62 },
            zIndex: 9999,
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          <Box
            sx={{
              mb: 0.75,
              px: 1.2,
              py: 0.45,
              borderRadius: "999px",
              border: "1px solid rgba(255,152,0,0.8)",
              background: "rgba(0,0,0,0.72)",
              backdropFilter: "blur(8px)",
              color: "white",
              fontSize: 12,
              fontWeight: 800,
              textAlign: "center",
              display: "inline-flex",
              alignItems: "center",
              gap: 0.7,
              boxShadow: "0 0 12px rgba(0,0,0,0.45)",
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: statusColor(pet.status),
                boxShadow: `0 0 10px ${statusColor(pet.status)}`,
              }}
            />
            {pet.status === "fallen"
              ? "I need help"
              : pet.status === "weak"
                ? "I feel weak"
                : animation === "eat"
                  ? "Eating"
                  : animation === "drink"
                    ? "Drinking"
                    : animation === "play"
                      ? "Playing"
                      : displayName}
          </Box>

          <Box
            sx={{
              width: { xs: 118, sm: 138 },
              height: { xs: 118, sm: 138 },
              borderRadius: "50%",
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background:
                "radial-gradient(circle at 30% 25%, rgba(255,255,255,0.24), rgba(255,255,255,0.08) 25%, rgba(0,170,255,0.08) 55%, rgba(255,152,0,0.16) 100%)",
              border: "2px solid rgba(255,255,255,0.24)",
              boxShadow:
                "0 0 25px rgba(0,160,255,0.22), 0 0 18px rgba(255,152,0,0.35), inset 0 0 20px rgba(255,255,255,0.08)",
              backdropFilter: "blur(6px)",
              overflow: "hidden",
              animation: pet.status === "fallen" ? "sphereStill 2s ease-in-out infinite" : "sphereFloat 4.2s ease-in-out infinite",
              "@keyframes sphereFloat": {
                "0%": { transform: "translateY(0px)" },
                "50%": { transform: "translateY(-10px)" },
                "100%": { transform: "translateY(0px)" },
              },
              "@keyframes sphereStill": {
                "0%": { transform: "translateY(0px)" },
                "50%": { transform: "translateY(-3px)" },
                "100%": { transform: "translateY(0px)" },
              },
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 14,
                left: 18,
                width: 24,
                height: 24,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.16)",
                filter: "blur(2px)",
              }}
            />

            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                animation:
                  animation === "idle"
                    ? "dogDrift 2.6s ease-in-out infinite"
                    : animation === "eat" || animation === "drink"
                      ? "dogActionNod 0.7s ease-in-out infinite"
                      : animation === "play"
                        ? "dogPlayHop 0.55s ease-in-out infinite"
                        : "dogDrift 2.6s ease-in-out infinite",
                "@keyframes dogDrift": {
                  "0%": { transform: "translateX(-6px) translateY(3px) rotate(-2deg)" },
                  "50%": { transform: "translateX(6px) translateY(-1px) rotate(2deg)" },
                  "100%": { transform: "translateX(-6px) translateY(3px) rotate(-2deg)" },
                },
                "@keyframes dogActionNod": {
                  "0%": { transform: "translateY(0px) scale(1)" },
                  "50%": { transform: "translateY(2px) scale(0.98)" },
                  "100%": { transform: "translateY(0px) scale(1)" },
                },
                "@keyframes dogPlayHop": {
                  "0%": { transform: "translateY(0px) scale(1)" },
                  "50%": { transform: "translateY(-7px) scale(1.03)" },
                  "100%": { transform: "translateY(0px) scale(1)" },
                },
              }}
            >
              <Box
                component="img"
                src={floatingImage}
                alt="Big Dog Bone Puppy"
                sx={{
                  width: { xs: 92, sm: 108 },
                  height: { xs: 92, sm: 108 },
                  objectFit: "contain",
                  filter: pet.status === "fallen" ? "grayscale(1) brightness(0.62)" : "drop-shadow(0 0 10px rgba(255,153,0,0.85))",
                }}
              />
            </Box>
          </Box>
        </Box>
      </Tooltip>

      {/* ======================================================
          MAIN PANEL
      ====================================================== */}
      <Modal open={openPanel} onClose={() => setOpenPanel(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "98vw", sm: "96vw", lg: "1180px", xl: "1280px" },
            height: { xs: "94vh", md: "88vh" },
            overflow: "hidden",
            background:
              "radial-gradient(circle at top, rgba(255,152,0,0.20), transparent 34%), linear-gradient(180deg, #151515, #050505)",
            border: "2px solid #ff9800",
            borderRadius: "24px",
            boxShadow: "0 0 45px rgba(255,152,0,0.50)",
            p: { xs: 1, sm: 1.4 },
            color: "white",
            display: "grid",
            gridTemplateRows: "auto auto 1fr auto",
            gap: 1,
          }}
        >
          {/* HEADER */}
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "flex-start", flexWrap: "wrap" }}>
            <Box>
              <Typography variant="h5" sx={{ color: "#ffb300", fontWeight: 950, lineHeight: 1.05 }}>
                🐶 Big Dog Bone Puppy
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.72)", fontSize: 12 }}>
                Powered by CityFarm resources · One-screen care dashboard
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
              <Chip
                label={statusText(pet.status)}
                sx={{ color: "black", fontWeight: 950, backgroundColor: statusColor(pet.status) }}
              />
              <Button size="small" variant="outlined" onClick={openCityFarm} sx={{ color: "#d5ffd7", borderColor: "#2e7d32", fontWeight: 950 }}>
                CityFarm
              </Button>
              <Button
                size="small"
                variant={showInstructions ? "contained" : "outlined"}
                onClick={() => setShowInstructions((prev) => !prev)}
                sx={{
                  color: showInstructions ? "black" : "#ffcc80",
                  backgroundColor: showInstructions ? "#ff9800" : "transparent",
                  borderColor: "#ff9800",
                  fontWeight: 950,
                }}
              >
                Instructions
              </Button>
            </Stack>
          </Box>

          {message && (
            <Alert severity={message.type} sx={{ py: 0.35 }}>
              {message.text}
            </Alert>
          )}

          {/* BODY */}
          <Box sx={{ minHeight: 0, overflow: "auto", pr: 0.25 }}>
            {!showInstructions ? (
              <Box
                sx={{
                  minHeight: 0,
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "275px 1fr 330px" },
                  gap: 1,
                }}
              >
                {/* LEFT */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
                  <InfoCard title="Puppy" sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <Box
                      sx={{
                        width: { xs: 175, md: 205 },
                        height: { xs: 175, md: 205 },
                        borderRadius: "50%",
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background:
                          "radial-gradient(circle at 30% 25%, rgba(255,255,255,0.24), rgba(255,255,255,0.08) 25%, rgba(0,170,255,0.08) 55%, rgba(255,152,0,0.16) 100%)",
                        border: "2px solid rgba(255,255,255,0.24)",
                        boxShadow:
                          "0 0 25px rgba(0,160,255,0.22), 0 0 18px rgba(255,152,0,0.35), inset 0 0 20px rgba(255,255,255,0.08)",
                        mb: 1,
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        component="img"
                        src={panelImage}
                        alt="Pet"
                        sx={{
                          width: { xs: 145, md: 168 },
                          height: { xs: 145, md: 168 },
                          objectFit: "contain",
                          filter: pet.status === "fallen" ? "grayscale(1) brightness(0.62)" : "drop-shadow(0 0 16px rgba(255,153,0,0.85))",
                        }}
                      />
                    </Box>

                    <Typography sx={{ fontWeight: 950, color: "#ffcc80", fontSize: 16 }}>{displayName}</Typography>
                    <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: 12 }}>{stageText(pet.stage)}</Typography>
                  </InfoCard>

                  <InfoCard title="Quick Info">
                    <SmallRow label="Birth name" value={pet.birthName} />
                    <SmallRow label="Level" value={pet.level} accent />
                    <SmallRow label="XP" value={pet.xp} />
                    <SmallRow label="Age" value={`${pet.ageDays || 0} days`} />
                    <SmallRow label="Status" value={statusText(pet.status)} accent />
                  </InfoCard>

                  <InfoCard title="Nickname">
                    <TextField
                      size="small"
                      fullWidth
                      value={nickname}
                      onChange={(e) => setNickname(e.target.value)}
                      placeholder="Puppy nickname"
                      sx={{
                        input: { color: "white" },
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": { borderColor: "#ff9800" },
                          "&:hover fieldset": { borderColor: "#ffb300" },
                          "&.Mui-focused fieldset": { borderColor: "#ffcc80" },
                        },
                      }}
                    />
                    <Button
                      fullWidth
                      disabled={loading}
                      variant="outlined"
                      onClick={saveNickname}
                      sx={{ mt: 0.8, color: "#ffb300", borderColor: "#ffb300", fontWeight: 950 }}
                    >
                      Save Nickname
                    </Button>
                  </InfoCard>
                </Box>

                {/* CENTER */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
                  <InfoCard title="Puppy Status">
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: { xs: 0, sm: 1.2 } }}>
                      <StatBar label="Life" icon="❤️" value={pet.life?.current || 0} />
                      <StatBar label="Health" icon="🩺" value={pet.health} />
                      <StatBar label="Hunger" icon="🍖" value={pet.hunger} />
                      <StatBar label="Thirst" icon="💧" value={pet.thirst} />
                      <StatBar label="Happiness" icon="😊" value={pet.happiness} />
                      <StatBar label="Energy" icon="⚡" value={pet.energy} />
                    </Box>
                  </InfoCard>

                  <InfoCard title="Smart Actions">
                    <Alert
                      severity={priorityHint.includes("Critical") ? "error" : priorityHint.includes("Attention") ? "warning" : "success"}
                      sx={{ mb: 1, py: 0.35 }}
                    >
                      {priorityHint}
                    </Alert>

                    {canUseActions ? (
                      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1 }}>
                        <ActionButton
                          disabled={loading}
                          urgency={hungerUrgency}
                          onClick={feedPet}
                          sx={{ backgroundColor: "#ff9800" }}
                        >
                          🍖 Feed
                        </ActionButton>

                        <ActionButton
                          disabled={loading}
                          urgency={thirstUrgency}
                          onClick={waterPet}
                          sx={{ backgroundColor: "#2196f3" }}
                        >
                          💧 Give Water
                        </ActionButton>

                        <ActionButton
                          disabled={loading}
                          urgency={happinessUrgency}
                          onClick={playPet}
                          sx={{ backgroundColor: "#4caf50" }}
                        >
                          🎾 Play
                        </ActionButton>

                        <ActionButton
                          disabled={loading}
                          readyPulseOn={claimReady}
                          onClick={claimDaily}
                          sx={{ backgroundColor: "#9c27b0" }}
                        >
                          🦴 Claim Daily
                        </ActionButton>
                      </Box>
                    ) : (
                      <Box sx={{ display: "grid", gap: 1 }}>
                        <Alert severity="error">Your puppy is fallen. Revive it to recover benefits.</Alert>
                        <ActionButton disabled={loading} onClick={() => revivePet("dinero")} sx={{ backgroundColor: "#ff9800" }}>
                          Revive with Mini-Bone
                        </ActionButton>
                        <ActionButton disabled={loading} onClick={() => revivePet("dineroReal")} sx={{ backgroundColor: "#f44336" }}>
                          Revive with Real HUESO
                        </ActionButton>
                      </Box>
                    )}
                  </InfoCard>

                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1 }}>
                    <InfoCard title="Cooldowns">
                      <SmallRow label="Feed" value={cooldowns?.feed?.text || "Ready"} />
                      <SmallRow label="Water" value={cooldowns?.water?.text || "Ready"} />
                      <SmallRow label="Play" value={cooldowns?.play?.text || "Ready"} />
                      <SmallRow label="Claim" value={cooldowns?.claim?.text || "Ready"} accent={claimReady} />
                    </InfoCard>

                    <InfoCard title="Current Level Benefit">
                      <SmallRow label={`Level ${currentLevelBenefit.level}`} value={currentLevelBenefit.benefit} accent />
                      <Typography sx={{ color: "rgba(255,255,255,0.62)", fontSize: 12 }}>{currentLevelBenefit.daily}</Typography>
                    </InfoCard>
                  </Box>
                </Box>

                {/* RIGHT */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
                  <InfoCard title="Big Dog Bone Balances">
                    <SmallRow label="Mini-Bone" value={fmtNumber(bdb?.dinero || 0)} accent />
                    <SmallRow label="Real HUESO" value={fmtNumber(bdb?.dineroReal || 0)} />
                    <SmallRow
                      label="Energy"
                      value={
                        bdb?.energia?.actual !== undefined
                          ? `${fmtNumber(bdb?.energia?.actual)} / ${fmtNumber(bdb?.energia?.maximo)}`
                          : "—"
                      }
                      accent
                    />
                  </InfoCard>

                  <InfoCard title="CityFarm Resources">
                    <SmallRow label="💧 Water" value={fmtNumber(waterQty)} accent={thirstUrgency !== "normal"} />
                    <SmallRow label="🍖 Croquettes" value={fmtNumber(foodQty)} accent={hungerUrgency !== "normal"} />
                    <Button fullWidth variant="contained" onClick={openCityFarm} sx={{ mt: 0.8, backgroundColor: "#2e7d32", fontWeight: 950 }}>
                      Produce More in CityFarm
                    </Button>
                  </InfoCard>

                  <InfoCard title="Current Benefit">
                    <SmallRow label="Status" value={benefit?.active ? "✅ Active" : "❌ Inactive"} accent={benefit?.active} />
                    <SmallRow label="Daily claim" value={`${fmtNumber(claimAmount)} Mini-Bone`} />
                    <SmallRow label="Mini-Bone bonus" value={`${benefit?.bdb?.miniHuesoBonusPercent || 0}%`} />
                    <SmallRow label="Energy bonus" value={`${benefit?.bdb?.energyBonusPercent || 0}%`} />
                  </InfoCard>

                  <InfoCard title="Neglect Warning">
                    <Typography sx={{ color: "rgba(255,255,255,0.78)", fontSize: 13 }}>
                      If you neglect your puppy, its stats can drop over time. Feed, water, and play buttons now blink according to urgency so the player knows what to do first.
                    </Typography>
                  </InfoCard>
                </Box>
              </Box>
            ) : (
              <Box>
                <Alert severity="info" sx={{ mb: 1 }}>
                  Your puppy lives in Big Dog Bone, but it uses Water and Croquettes produced in CityFarm.
                </Alert>

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1 }}>
                  <InfoCard title="Daily Care">
                    <SmallRow label="Feed" value="Uses 1 Croquettes from CityFarm" />
                    <SmallRow label="Water" value="Uses 1 Water from CityFarm" />
                    <SmallRow label="Play" value="Improves happiness" />
                    <SmallRow label="Daily claim" value="Gives Mini-Bone when conditions are met" />
                  </InfoCard>

                  <InfoCard title="How to Keep Benefits Active">
                    <SmallRow label="Hunger" value="Keep above 55%" />
                    <SmallRow label="Thirst" value="Keep above 55%" />
                    <SmallRow label="Happiness" value="Keep above 45%" />
                    <SmallRow label="Health" value="Keep above 65%" />
                    <SmallRow label="Life" value="Keep above 25%" />
                  </InfoCard>
                </Box>

                <InfoCard title="Neglect Rule" sx={{ mt: 1 }}>
                  <Typography sx={{ color: "rgba(255,255,255,0.76)", fontSize: 13 }}>
                    If the puppy is forgotten, hunger, thirst, happiness, health, and life go down over time. If it reaches a critical state, it can become weak or fallen. XP or level loss must be enforced by the backend for full security.
                  </Typography>
                </InfoCard>

                <InfoCard title="Benefits by Level" sx={{ mt: 1 }}>
                  {benefitLevels.map((row, index) => (
                    <Box key={row.level}>
                      <SmallRow label={`Level ${row.level}`} value={row.benefit} />
                      <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: 12, mb: 0.6 }}>{row.daily}</Typography>
                      {index !== benefitLevels.length - 1 && <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", mb: 0.6 }} />}
                    </Box>
                  ))}
                </InfoCard>

                <Button variant="contained" onClick={openCityFarm} sx={{ mt: 1, backgroundColor: "#2e7d32", fontWeight: 950 }}>
                  Open CityFarm to Produce Water and Croquettes
                </Button>
              </Box>
            )}
          </Box>

          {/* FOOTER */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: 12 }}>
              {lastUpdated ? `Updated: ${lastUpdated.toLocaleTimeString()}` : "Not updated yet"}
            </Typography>

            <Button
              disabled={loading}
              variant="text"
              onClick={() => refreshPet({ silent: false, keepMessage: false, syncGlobal: true })}
              sx={{ color: "#ffcc80", fontWeight: 950 }}
            >
              {loading ? "Updating..." : "Refresh All"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}
